import { useState } from 'react';
import { SidecarFormats, sidecarFormatsField } from '../../subtitles/SidecarFormats';
import { DEFAULT_SIDECAR_FORMATS, SIDECAR_LABELS, type SidecarFormat } from '@prism/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Download, FileText, Loader2, Share, X } from 'lucide-react';
import {
  EXPORT_PRESETS, exportFormat, exportFrameInfo, formatsOf, presetProblem, formatDuration, allClips, markRange, type SequenceRange,
  type CheckinDTO, type ExportPreset, type ExportDTO, type FormatRef, type HandoffDTO, type ResolvedFormat, type SequenceDocument,
} from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { PresetSelect } from '../../components/PresetSelect';
import { TableScroll } from '../../components/TableScroll';
import { DestinationSelect, downloadNote } from '../../components/DestinationSelect';
import { useCategories, useExportDestinations, useExports, useHandoffs, useMe, type SequenceSource } from '../../api/hooks';
import { HandoffRow, useCheckinDialog } from '../../components/SendToEdit';
import { Link } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { PreflightProblems, RightsCheckBlock, preflightBlocks, preflightRestorable, usePreflight, useRightsCheck } from '../../components/Preflight';
import { lineageTrackLabel } from './lineageTrack';
import { PlaceholderPicker } from '../../components/PlaceholderPicker';
import type { PlaceholderTarget } from '../../upload/uploader';
import { bytes, relative } from '../../lib/format';
import { queryKeys } from '../../api/queryKeys';
import { waitedFor } from '../../components/JobProgress';
import { LoadMore } from '../../components/LoadMore';
import { exportFormatsBody, formatColor, formatRowLabel, groupExportRows, presetsForFormat, type FormatChoice } from './edit-formats';

/** The Formats checklist of a multi-format sequence: the primary and each format, each with its own preset. */
function FormatsChecklist({ doc, formats, choices, setChoices }: {
  doc: SequenceDocument; formats: ResolvedFormat[]; choices: Record<FormatRef, FormatChoice>; setChoices: (c: Record<FormatRef, FormatChoice>) => void;
}) {
  return (
    <div className="field" data-testid="export-formats">
      <label>Formats</label>
      <div className="export-formats">
        {formats.map((f) => {
          const c = choices[f.id]!;
          const allowed = presetsForFormat(f, doc.frameRate);
          const info = exportFrameInfo(c.preset, { width: f.width, height: f.height, frameRate: doc.frameRate });
          const group = (g: 'web' | 'broadcast') => allowed.filter((k) => EXPORT_PRESETS[k].group === g);
          return (
            <div key={f.id} className={`export-format-row ${c.on ? '' : 'off'}`} data-testid="export-format-row" data-format={f.id}>
              <label className="toggle-row" style={{ ['--format-color' as string]: formatColor(f.index) }}>
                <input type="checkbox" checked={c.on} onChange={(e) => setChoices({ ...choices, [f.id]: { ...c, on: e.target.checked } })} data-testid={`export-format-${f.id}`} />
                <span className="format-swatch" style={{ background: formatColor(f.index) }} aria-hidden />
                <span><strong>{f.label}</strong>{f.isPrimary ? ' · primary' : ''} <span className="faint mono">{f.width}×{f.height}</span></span>
              </label>
              <select className="select" value={c.preset} disabled={!c.on} aria-label={`Preset for ${f.label}`} data-testid={`export-format-preset-${f.id}`}
                onChange={(e) => setChoices({ ...choices, [f.id]: { ...c, preset: e.target.value as ExportPreset } })}>
                <optgroup label="Web and review">{group('web').map((k) => <option key={k} value={k}>{EXPORT_PRESETS[k].label}</option>)}</optgroup>
                {group('broadcast').length > 0 && <optgroup label="Broadcast">{group('broadcast').map((k) => <option key={k} value={k}>{EXPORT_PRESETS[k].label}</option>)}</optgroup>}
              </select>
              <span className="help mono" data-testid={`export-format-frame-${f.id}`}>{info.label}</span>
            </div>
          );
        })}
      </div>
      <span className="help">Each chosen format renders from the same saved revision, in its own framing. Broadcast formats have a fixed 1920×1080 frame, so they are offered for 16:9 formats only.</span>
    </div>
  );
}

const STEP_LABEL: Record<string, string> = { recording: 'Waiting for the recording to end', restoring: 'Waiting for originals from the archive', fetching: 'Fetching originals from site', graphics: 'Rendering graphics', subtitles: 'Rendering subtitles', compose: 'Rendering sequence', finalize: 'Finishing', done: 'Done' };

export function ExportDialog({ sequenceId, name, revision, doc, sources, flush, onClose }: {
  sequenceId: string; name: string; revision: number; doc: SequenceDocument; sources: SequenceSource[]; flush: () => Promise<void>; onClose: () => void;
}) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<ExportPreset>('h264-1080p');
  const [quality, setQuality] = useState<'original' | 'proxy'>('original');
  const [subs, setSubs] = useState<'none' | 'burn' | 'sidecar' | 'both'>(doc.subtitles?.cues.length ? 'sidecar' : 'none');
  // 1.9.0: which sidecar files (SRT and WebVTT by default).
  const [sidecars, setSidecars] = useState<SidecarFormat[]>([...DEFAULT_SIDECAR_FORMATS]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 1.7.4: a video file or the EDL (the top bar's EDL button moved here), of the whole sequence or its In to Out.
  const [kind, setKind] = useState<'video' | 'edl'>('video');
  const hasMarks = Boolean(markRange(doc));
  const [rangeChoice, setRange] = useState<SequenceRange>('all');
  const range: SequenceRange = hasMarks ? rangeChoice : 'all';
  const destinations = useExportDestinations().data?.items ?? [];
  const [destination, setDestination] = useState('');
  const [asAsset, setAsAsset] = useState(false);
  const [assetTitle, setAssetTitle] = useState('');
  const [assetClass, setAssetClass] = useState('');
  // Save as: a new asset, or into a placeholder (0.91.0), which keeps its title, metadata and class.
  const [placeholder, setPlaceholder] = useState<PlaceholderTarget | null>(null);
  const into = asAsset ? placeholder : null;
  const categories = useCategories();
  const canUpload = Boolean(useMe().data?.permissions.includes('asset.upload'));
  // Only media the edit uses (multicam angles that are not cut in do not count).
  const used = new Set(allClips(doc).map((c) => c.clip.versionId));
  // A source still recording (1.0.0-rc.1) is not missing: the export waits for the recording to end.
  const missing = sources.filter((s) => used.has(s.versionId) && !s.originalAvailable && !s.live);
  const onSite = sources.filter((s) => used.has(s.versionId) && s.originalOnSite);
  const hasCues = Boolean(doc.subtitles?.cues.length);
  // The server checks the saved revision: sources at this quality and the destination.
  const preflight = usePreflight({ kind: 'sequence-export', sequenceId, quality, locationId: asAsset ? null : destination || null, asAsset: asAsset ? 'true' : null, revision: String(revision) });
  // 1.7.0: a sequence with formats chooses which to render, each with its own preset (one request, one row each).
  const formats = formatsOf(doc);
  const multi = formats.length > 1;
  const [choices, setChoices] = useState<Record<FormatRef, FormatChoice>>(() => Object.fromEntries(formats.map((f) => {
    const allowed = presetsForFormat(f, doc.frameRate);
    return [f.id, { on: true, preset: allowed.includes('h264-1080p') ? 'h264-1080p' : allowed[0] ?? 'h264-1080p' }];
  })));
  const chosen = multi ? exportFormatsBody(formats, Object.fromEntries(formats.map((f) => [f.id, choices[f.id] ?? { on: false, preset: 'h264-1080p' as ExportPreset }]))) : [];
  const firstPreset: ExportPreset = multi ? chosen[0]?.preset ?? preset : preset;
  const presetBlocked = multi ? chosen.length === 0 || chosen.some((c) => presetProblem(c.preset, doc.frameRate)) : Boolean(presetProblem(preset, doc.frameRate));
  // 1.8.0: the rights answers (confirm, a rights manager's override) the export sends with its start.
  // 1.8.1: saving into PRISM (a new asset, or into a placeholder) is not checked for rights: the new asset carries its
  // sources' rights (genealogy), and its own exports out of PRISM are checked. No rights block, no answers sent.
  const rights = useRightsCheck(asAsset ? [] : preflight.data?.problems);

  async function start(restore = false) {
    setBusy(true);
    setError(null);
    try {
      await flush();
      const cur = await api<{ revision: number }>(`/api/v1/sequences/${sequenceId}`);
      await api<ExportDTO>(`/api/v1/sequences/${sequenceId}/exports`, { method: 'POST', json: { ...(range === 'marks' ? { range } : {}), preset: firstPreset, ...(multi ? { formats: chosen } : {}), quality, subtitleMode: hasCues ? subs : 'none', ...(hasCues && (subs === 'sidecar' || subs === 'both') && sidecarFormatsField(sidecars) ? { sidecarFormats: sidecarFormatsField(sidecars) } : {}), revision: cur.revision, locationId: asAsset ? null : destination || null, asAsset: asAsset && !into ? { title: assetTitle.trim() || undefined, categoryId: assetClass || null } : null, ...(into ? { placeholderAssetId: into.id } : {}), ...(restore ? { restore: true } : {}), ...(asAsset ? {} : rights.field()) } });
      await qc.invalidateQueries({ queryKey: queryKeys.exports(sequenceId) });
      toast(restore ? 'Restoring the originals from the archive; the export starts when they are back'
        : into ? `Rendering; ${into.assetCode} gets its media when it is ready` : asAsset ? 'Rendering; the new asset appears in the library when it is ready' : 'Export started');
      onClose();
    } catch (err) {
      setError(!asAsset && rights.takeError(err) ? 'Check the rights below first' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  /** The EDL of the saved sequence (pending edits saved first), downloaded at once. */
  async function downloadEdl() {
    setBusy(true);
    setError(null);
    try {
      await flush();
      const a = document.createElement('a');
      a.href = `/api/v1/sequences/${sequenceId}/edl${range === 'marks' ? '?range=marks' : ''}`;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const kindAndRange = (
    <div className="field-row">
      <div className="field"><label>Export as</label>
        <div className="segmented" role="radiogroup" aria-label="Export as" data-testid="export-kind">
          <button aria-pressed={kind === 'video'} onClick={() => setKind('video')} data-testid="export-kind-video">Video file</button>
          <button aria-pressed={kind === 'edl'} onClick={() => setKind('edl')} data-testid="export-kind-edl">Edit decision list (CMX 3600 EDL)</button>
        </div>
      </div>
      <div className="field"><label>Range</label>
        <div className="segmented" role="radiogroup" aria-label="Range" data-testid="export-range">
          <button aria-pressed={range === 'all'} onClick={() => setRange('all')} data-testid="export-range-all">Whole sequence</button>
          <button aria-pressed={range === 'marks'} onClick={() => setRange('marks')} disabled={!hasMarks} data-testid="export-range-marks"
            title={hasMarks ? 'From the sequence In to its Out' : 'Mark a sequence In or Out (I, O) first'}>In to Out</button>
        </div>
      </div>
    </div>
  );

  if (kind === 'edl') {
    return (
      <Modal title="Export an EDL" onClose={onClose} wide
        actions={<><button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => void downloadEdl()} disabled={busy || !doc.video.length} data-testid="download-edl">
            {busy ? <Loader2 size={14} className="spin" /> : <FileText size={14} />} Download EDL
          </button></>}>
        {error && <div className="banner error">{error}</div>}
        {kindAndRange}
        <p className="help" style={{ margin: 0 }} data-testid="export-edl-help">A CMX 3600 edit decision list of the saved sequence{range === 'marks' ? ', from its In to its Out' : ''}: V1 with its sound, A2, transitions and markers as locators; the other tracks and graphics as comments. For a full timeline with every track, use Send to edit.</p>
      </Modal>
    );
  }

  return (
    <Modal title={into ? 'Save sequence into a placeholder' : asAsset ? 'Save sequence as a new asset' : 'Export sequence'} onClose={onClose} wide
      actions={<><button className="btn ghost" onClick={onClose}>Cancel</button>
        {preflightRestorable(preflight) && (
          <button className="btn primary" onClick={() => void start(true)} disabled={busy || !doc.video.length || presetBlocked || (!asAsset && !rights.ready)} data-testid="restore-and-export">
            {busy ? <Loader2 size={14} className="spin" /> : <Share size={14} />} Restore and {asAsset ? 'save' : 'export'}
          </button>
        )}
        <button className="btn primary" onClick={() => void start()} disabled={busy || !doc.video.length || (quality === 'original' && missing.length > 0) || presetBlocked || preflightBlocks(preflight) || (kind === 'video' && !asAsset && !rights.ready)} data-testid="start-export">
          {busy ? <Loader2 size={14} className="spin" /> : <Share size={14} />} {into ? 'Save into placeholder' : asAsset ? 'Save as asset' : 'Export'}{multi && chosen.length > 1 ? ` ${chosen.length} formats` : ''}
        </button></>}>
      {error && <div className="banner error">{error}</div>}
      {kindAndRange}
      {canUpload && (
        <div className="segmented" role="radiogroup" aria-label="Export or save as an asset" style={{ alignSelf: 'flex-start' }}>
          <button aria-pressed={!asAsset} onClick={() => setAsAsset(false)}>Export</button>
          <button aria-pressed={asAsset} onClick={() => setAsAsset(true)} data-testid="export-as-asset">Save as an asset</button>
        </div>
      )}
      {multi && <FormatsChecklist doc={doc} formats={formats} choices={choices} setChoices={setChoices} />}
      <div className="field-row">
        {!multi && <div className="field"><label htmlFor="ex-preset">Format</label>
          <PresetSelect id="ex-preset" value={preset} onChange={setPreset} frameRate={doc.frameRate} />
          {!presetProblem(preset, doc.frameRate) && (() => {
            // What the file is (1.0.0-rc.11): its frame and scan, and how a sequence of another shape sits in a fixed frame.
            const f = exportFrameInfo(preset, doc);
            return (
              <>
                <span className="help mono" data-testid="export-frame">{f.label}</span>
                {f.note && <span className="help" data-testid="export-frame-note">{f.note}</span>}
                {EXPORT_PRESETS[preset].interlaced && <span className="help" data-testid="export-interlaced-note">Rendered at the field rate, so it takes about twice as long as a progressive export.</span>}
              </>
            );
          })()}
        </div>}
        {hasCues && (
          <div className="field"><label htmlFor="ex-subs">Subtitles</label>
            <select id="ex-subs" className="select" value={subs} onChange={(e) => setSubs(e.target.value as typeof subs)} data-testid="export-subs">
              <option value="none">None</option>
              <option value="sidecar">Sidecar files</option>
              <option value="burn">Burned into the picture</option>
              <option value="both">Burned in and sidecar</option>
            </select>
          </div>
        )}
        {hasCues && (subs === 'sidecar' || subs === 'both') && <SidecarFormats value={sidecars} onChange={setSidecars} testId="export-sidecars" />}
      </div>
      {!asAsset && (
        <div className="field"><label htmlFor="ex-dest">Export to</label>
          <DestinationSelect id="ex-dest" value={destination} onChange={setDestination} destinations={destinations} />
        </div>
      )}
      {asAsset && (
        <div className="field" data-testid="export-save-as">
          <label>Save as</label>
          <PlaceholderPicker value={placeholder} onChange={setPlaceholder} />
        </div>
      )}
      {into && (
        <span className="help" data-testid="export-into-placeholder">The render becomes the first media of <span className="mono">{into.assetCode}</span> · {into.title}, like an upload into it. Its title, metadata and class stay as they are; it is processed like an upload and related to the media it uses. If the placeholder gets media before the render is done, nothing is overwritten and the render stays downloadable here.</span>
      )}
      {asAsset && multi && chosen.length > 1 && (
        <span className="help" data-testid="export-formats-asset-note">One asset: the first format ({formats.find((f) => f.id === chosen[0]!.formatId)?.label}) is the original, the others are masters with a variant for their shape.</span>
      )}
      {asAsset && !into && (
        <div className="field">
            <div className="field-row">
              <div className="field"><label htmlFor="ex-asset-title">Title</label><input id="ex-asset-title" className="input" value={assetTitle} placeholder={name} onChange={(e) => setAssetTitle(e.target.value)} /></div>
              <div className="field"><label htmlFor="ex-asset-class">Class</label>
                <select id="ex-asset-class" className="select" value={assetClass} onChange={(e) => setAssetClass(e.target.value)}>
                  <option value="">No class</option>
                  {(categories.data?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            </div>
          <span className="help">The render is stored by the storage tiers like any ingest (not in an export location) and processed like an upload. Its description says which sequence it came from, it takes the keywords of the media it uses, and it is related to that media.</span>
        </div>
      )}
      <div className="field">
        <label>Source quality</label>
        <div className="segmented" role="radiogroup">
          <button aria-pressed={quality === 'original'} onClick={() => setQuality('original')}>Originals (full quality)</button>
          <button aria-pressed={quality === 'proxy'} onClick={() => setQuality('proxy')} data-testid="proxy-quality">Proxies (review quality)</button>
        </div>
      </div>
      <PreflightProblems q={preflight} what={asAsset ? 'Saving as an asset' : 'This export'} />
      {!asAsset && <RightsCheckBlock check={rights} />}
      {(preflight.data?.waitsForRecording?.length ?? 0) > 0 && (
        <div className="banner info" data-testid="export-waits-recording"><div className="banner-body">
          <strong>Waiting for the recording to end</strong>
          <span>{preflight.data!.waitsForRecording!.map((r) => `“${r.title}”`).join(', ')} {preflight.data!.waitsForRecording!.length === 1 ? 'is' : 'are'} still recording. The export starts when the recording has ended and its file is ingested, and renders from the finished {quality === 'original' ? 'original' : 'proxy'}.</span>
        </div></div>
      )}
      {quality === 'original' && missing.length > 0 && !preflight.data && (
        <div className="banner warning"><div className="banner-body">
          <strong>Some originals cannot be used</strong>
          {missing.map((m) => <span key={m.versionId}>{m.title}: {m.originalProblem}</span>)}
          <span className="muted">Transfer or reconnect them first, or choose a proxy-quality export. Proxy exports are labelled as such in their lineage.</span>
        </div></div>
      )}
      {quality === 'original' && onSite.length > 0 && (
        <div className="banner info" data-testid="export-on-site">{onSite.length === 1 ? `The original of “${onSite[0]!.title}” is` : `${onSite.length} originals are`} on a storage agent ({[...new Set(onSite.map((s) => s.originalOnSite))].join(', ')}). The export fetches {onSite.length === 1 ? 'it' : 'them'} first, renders, and removes the temporary copies afterwards.</div>
      )}
      {quality === 'proxy' && <div className="banner info">This export is made from 720p browser proxies and is recorded as proxy quality. Use it for review, not delivery.</div>}
      <p className="faint" style={{ margin: 0, fontSize: 12 }}>The export renders the current saved revision with exact frame timing on the server. You can keep editing while it runs.</p>
    </Modal>
  );
}

function Lineage({ e, doc, onClose }: { e: ExportDTO; doc?: SequenceDocument; onClose: () => void }) {
  const L = e.lineage!;
  return (
    <Modal wide title="What went into this export" onClose={onClose} actions={<button className="btn" onClick={onClose}>Close</button>}>
      <dl className="kv">
        <dt>Sequence</dt><dd>{L.sequence.name} · revision {L.sequence.revision} · {L.sequence.versionLabel}</dd>
        {e.formatLabel && <><dt>Format</dt><dd data-testid="lineage-format">{e.formatLabel}</dd></>}
        <dt>Render</dt><dd>{exportFormat({ preset: L.render.preset }).label} · {L.render.width}×{L.render.height} · {L.render.quality === 'proxy' ? 'proxy quality' : 'from originals'}</dd>
        <dt>Tools</dt><dd className="mono" style={{ fontSize: 11.5 }}>{L.render.ffmpeg}{L.render.renderer ? ` · ${L.render.renderer}` : ''}</dd>
        <dt>Subtitles</dt><dd>{L.subtitles.mode === 'none' ? 'None' : `${L.subtitles.mode} · ${L.subtitles.language} · ${L.subtitles.cues} cues`}</dd>
      </dl>
      <p className="section-label">Sources</p>
      <TableScroll>
      <table className="table" data-testid="lineage-sources">
        <thead><tr><th>Track</th><th>Asset</th><th>Version</th><th>Range</th><th>Checksum</th></tr></thead>
        <tbody>
          {L.sources.map((s) => (
            <tr key={s.clipId}><td className="nowrap" data-testid="lineage-track">{lineageTrackLabel(s, doc)}</td><td className="wrap" style={{ minWidth: 140 }}>{s.assetTitle}</td><td className="wrap">v{s.versionNumber} · {s.representation}</td>
              <td className="mono nowrap">{formatDuration(s.sourceInTicks)}–{formatDuration(s.sourceOutTicks)}</td><td className="mono faint nowrap">{s.checksumSha256?.slice(0, 12)}…</td></tr>
          ))}
        </tbody>
      </table>
      </TableScroll>
      {L.graphics.length > 0 && (
        <>
          <p className="section-label">Graphics</p>
          {L.graphics.map((g) => <div key={g.instanceId} className="loc-row"><strong>{g.templateId} v{g.templateVersion}</strong><span className="key">{Object.values(g.data).join(' · ')}</span><span className="mono faint">{g.templateChecksumSha256.slice(0, 12)}…</span></div>)}
        </>
      )}
    </Modal>
  );
}

/**
 * A sequence's exports and (rc.9 fixes) its hand-offs, newest first: a hand-off is a row of kind Send to edit with its
 * live progress (HandoffRow). While older exports are still to load, only hand-offs newer than the oldest shown are in.
 * `tabId`: the editor tab's, so Check in can apply a new version with its edit lock. It is the history of what was sent:
 * hand-offs removed from the Import list, missing and cleaned up ones stay in it, with a note.
 */
export function ExportList({ sequenceId, tabId, onApplied, doc }: { sequenceId: string; tabId?: string; onApplied?: (c: CheckinDTO) => void; doc?: SequenceDocument }) {
  const q = useExports(sequenceId);
  const handoffs = useHandoffs(sequenceId, { includeRemoved: true }).data?.items ?? [];
  const checkin = useCheckinDialog(tabId, onApplied);
  const [lineage, setLineage] = useState<ExportDTO | null>(null);
  const exports = q.data?.items ?? [];
  const oldest = q.hasMore && exports.length ? exports.at(-1)!.createdAt : '';
  const rows: Array<{ export: ExportDTO } | { handoff: HandoffDTO }> = [
    ...exports.map((e) => ({ export: e })), ...handoffs.filter((h) => h.createdAt >= oldest).map((h) => ({ handoff: h })),
  ].sort((a, b) => ('export' in b ? b.export.createdAt : b.handoff.createdAt).localeCompare('export' in a ? a.export.createdAt : a.handoff.createdAt));
  // 1.7.0: exports of one multi-format request are one group.
  const items: Array<ReturnType<typeof groupExportRows>[number] | { handoff: HandoffDTO }> = [];
  const pending: ExportDTO[] = [];
  const flush = () => { if (pending.length) items.push(...groupExportRows(pending.splice(0))); };
  for (const r of rows) { if ('export' in r) pending.push(r.export); else { flush(); items.push(r); } }
  flush();
  if (!rows.length) return <p className="muted" style={{ margin: 0 }} data-testid="export-list-empty">No exports or hand-offs yet.</p>;
  return (
    <div className="steps" data-testid="export-list">
      {checkin.dialog}
      {items.map((r) => {
        if ('handoff' in r) return <HandoffRow key={`h-${r.handoff.id}`} h={r.handoff} onCheckin={checkin.open} />;
        if (r.kind === 'group') {
          // 1.7.0: the rows of one multi-format request, together.
          const done = r.exports.filter((e) => e.status === 'succeeded').length;
          return (
            <div className="export-group" key={r.key} data-testid="export-group" data-count={r.exports.length}>
              <div className="export-group-head"><span className="step-title" data-testid="export-group-label">{r.label}</span><span className="faint">{done}/{r.exports.length} done</span></div>
              {r.exports.map((e) => <ExportRow key={e.id} e={e} grouped onLineage={setLineage} />)}
            </div>
          );
        }
        return <ExportRow key={r.export.id} e={r.export} onLineage={setLineage} />;
      })}
      <LoadMore list={q} shown={exports.length} noun="exports" />
      {lineage && <Lineage e={lineage} doc={doc} onClose={() => setLineage(null)} />}

    </div>
  );
}

/** One export of the list; `grouped`: a row of a multi-format request, labelled by its format first. */
function ExportRow({ e, grouped = false, onLineage: setLineage }: { e: ExportDTO; grouped?: boolean; onLineage: (e: ExportDTO) => void }) {
  return (
    <div className="step" data-status={e.status} data-testid="export-row" data-format={e.formatId ?? (e.formatLabel != null ? 'primary' : undefined)}>
      <span className={`step-icon ${e.status === 'succeeded' ? 'succeeded' : e.status === 'failed' ? 'failed' : 'running'}`}>
        {e.status === 'succeeded' ? <Check size={12} strokeWidth={3} /> : e.status === 'failed' ? <X size={12} strokeWidth={3} /> : <Loader2 size={12} className="spin" />}
      </span>
      <div className="step-main">
        <span className="step-title">{grouped || e.formatLabel ? <span className="export-format-label" data-testid="export-row-format">{formatRowLabel(e)} · </span> : null}{exportFormat(e).label}{e.quality === 'proxy' ? ' · proxy quality' : ''}</span>
        <span className="step-sub">
          {e.waiting ? <span data-testid="export-queued">Queued · waiting for a free worker{e.waitingSince ? ` · ${waitedFor(e.waitingSince)}` : ''}</span>
            : e.status === 'running' || e.status === 'queued' ? `${STEP_LABEL[e.step ?? ''] ?? 'Queued'}${e.percent != null ? ` · ${Math.round(e.percent)}%` : ''}` : relative(e.finishedAt ?? e.createdAt)}
          {e.output?.sizeBytes ? ` · ${bytes(e.output.sizeBytes)}` : ''}{e.requestedBy ? ` · ${e.requestedBy}` : ''}
          {downloadNote(e.download) ? ` · ${downloadNote(e.download)}` : ''}
          {e.destination ? ` · to ${e.destination.name}${e.destination.delivery === 'pending' && e.status === 'succeeded' ? ' (delivering…)' : e.destination.delivery === 'delivered' ? ' (delivered)' : ''}` : ''}
        </span>
        {e.destination?.delivery === 'failed' && <span className="step-sub" style={{ color: 'var(--danger)' }}><AlertTriangle size={11} /> Delivery failed: {e.destination.error}</span>}
        {e.error && <span className="step-sub" style={{ color: 'var(--danger)' }}><AlertTriangle size={11} /> {e.error.message}</span>}
        {e.asAsset && e.status === 'succeeded' && (() => {
          const ph = e.asAsset.placeholder;
          const what = ph ? `into ${ph.assetCode ?? 'the placeholder'}` : 'as an asset';
          return e.asAsset.assetId
            ? <span className="step-sub"><Link to={`/assets/${e.asAsset.assetId}`} data-testid="export-asset-link">Saved {what}</Link></span>
            : e.asAsset.waiting ? <span className="step-sub" data-testid="export-asset-waiting">Waiting for the main format, then saved as a master of its asset…</span>
            : e.asAsset.error ? <span className="step-sub" style={{ color: 'var(--danger)' }} data-testid="export-asset-error"><AlertTriangle size={11} /> Not saved {what}: {e.asAsset.error}</span>
            : <span className="step-sub">Saving {what}…</span>;
        })()}
        {e.output && (
          <span style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <a className="btn sm" href={e.output.url ?? undefined} download data-testid="export-download"><Download size={13} /> {exportFormat(e).extension.toUpperCase()}</a>
            {e.output.sidecars.map((s) => <a key={s.format} className="btn ghost sm" href={s.url} download><FileText size={13} /> {SIDECAR_LABELS[s.format] ?? String(s.format).toUpperCase()}</a>)}
            {e.lineage && <button className="btn ghost sm" onClick={() => setLineage(e)} data-testid="export-lineage">Lineage</button>}
          </span>
        )}
      </div>
    </div>
  );
}
